import { getAPIUrl } from "config/api";
import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { FileUpload } from "primereact/fileupload";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressBar } from "primereact/progressbar";
import { useContext, useRef } from "react";
import { useSelector } from "react-redux";
import {
  useDeleteRuleAttachmentMutation,
  useUploadRuleAttachmentMutation,
} from "store/queries/ruleAttachments";
import { authSelector } from "store/slices/auth";

import {
  ALLOWED_ATTACHMENT_EXTENSIONS,
  MAX_ATTACHMENTS_PER_RULE,
  MAX_CONTRACT_LANGUAGE_LENGTH,
  MAX_FILE_SIZE_BYTES,
  RULE_ROOTS,
} from "./constants";
import { IRuleAttachment, RuleType } from "./types";

interface IContractLanguageSectionProps {
  ruleType: RuleType;
  /** Rule id — when omitted (Create mode), only the textarea is shown. */
  ruleId?: string;
  attachments?: IRuleAttachment[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const formatFileSize = (bytes: number): string => {
  const kb = bytes / 1024;
  return kb >= 1024 ? `${(kb / 1024).toFixed(2)} MB` : `${kb.toFixed(2)} KB`;
};

export function ContractLanguageSection({
  ruleType,
  ruleId,
  attachments,
  value,
  onChange,
  disabled,
}: IContractLanguageSectionProps) {
  const { current: toastElement } = useContext(ToastContext);
  const { token } = useSelector(authSelector);
  const fileUploadRef = useRef<FileUpload>(null);
  const [uploadAttachment, { isLoading: isUploading }] =
    useUploadRuleAttachmentMutation();
  const [deleteAttachment] = useDeleteRuleAttachmentMutation();

  const isEditMode = !!ruleId;
  const charCount = value?.length ?? 0;
  const isOverLimit = charCount > MAX_CONTRACT_LANGUAGE_LENGTH;
  const attachmentCount = attachments?.length ?? 0;
  const reachedAttachmentLimit = attachmentCount >= MAX_ATTACHMENTS_PER_RULE;

  const handleSelect = async (e: { files: File[] | ArrayLike<File> }) => {
    if (!ruleId) return;
    const files = Array.from(e.files);
    const remainingSlots = MAX_ATTACHMENTS_PER_RULE - attachmentCount;
    const accepted = files.slice(0, Math.max(0, remainingSlots));
    const rejected = files.length - accepted.length;
    if (rejected > 0) {
      toastElement?.show({
        severity: "warn",
        summary: "Attachment limit reached",
        detail: `Maximum ${MAX_ATTACHMENTS_PER_RULE} attachments per rule. Skipped ${rejected} file(s).`,
      });
    }
    await Promise.all(
      accepted.map((file) =>
        uploadAttachment({ ruleType, ruleId, file })
          .unwrap()
          .then(() => {
            toastElement?.show({
              severity: "success",
              summary: "Uploaded",
              detail: `${file.name} uploaded.`,
            });
          })
          .catch((err: any) => {
            toastElement?.show({
              severity: "error",
              summary: "Upload failed",
              detail:
                err?.data?.message ||
                err?.data?.messages?.[0] ||
                `Failed to upload ${file.name}.`,
            });
          })
      )
    );
    fileUploadRef.current?.clear();
  };

  const handleDelete = async (attachment: IRuleAttachment) => {
    if (!ruleId) return;
    try {
      await deleteAttachment({
        ruleType,
        ruleId,
        attachmentId: attachment.id,
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Removed",
        detail: "Attachment removed.",
      });
    } catch (err: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          err?.data?.message ||
          err?.data?.messages?.[0] ||
          "Failed to remove attachment.",
      });
    }
  };

  const handleDownload = async (attachment: IRuleAttachment) => {
    if (!ruleId) return;
    try {
      const apiBase = getAPIUrl().replace(/\/+$/, "");
      const response = await fetch(
        `${apiBase}/${RULE_ROOTS[ruleType]}/${ruleId}/attachments/${attachment.id}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        throw new Error(
          `Download failed: ${response.status} ${response.statusText}`
        );
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = attachment.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Attachment download failed", err);
      toastElement?.show({
        severity: "error",
        summary: "Download failed",
        detail:
          err instanceof Error ? err.message : "Failed to download attachment.",
      });
    }
  };

  const actionsTemplate = (rowData: IRuleAttachment) => (
    <>
      <Button
        icon="pi pi-download"
        className="p-button-rounded p-button-text p-button-sm mr-1"
        tooltip="Download"
        tooltipOptions={{ position: "top" }}
        onClick={() => handleDownload(rowData)}
      />
      <Button
        icon="pi pi-times"
        className="p-button-rounded p-button-text p-button-danger p-button-sm"
        tooltip="Remove"
        tooltipOptions={{ position: "top" }}
        onClick={() => handleDelete(rowData)}
      />
    </>
  );

  return (
    <Card title="Contract Language" className="mb-4">
      <p className="text-600 mb-3">
        Add the exact contract wording that justifies this rule. This text and
        any attachments will appear under the relevant rule on the claim&apos;s
        Expected Amounts tab so reviewers can see why this rule applies.
      </p>

      <div className="field">
        <InputTextarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={6}
          className="w-full"
          autoResize
          maxLength={MAX_CONTRACT_LANGUAGE_LENGTH}
          placeholder="Paste the relevant contract language here..."
          disabled={disabled}
        />
        <small className={isOverLimit ? "p-error" : "text-500"}>
          {charCount.toLocaleString()} /{" "}
          {MAX_CONTRACT_LANGUAGE_LENGTH.toLocaleString()} characters
        </small>
      </div>

      {isEditMode && (
        <div className="mt-4">
          <h6 className="mb-2">
            Attachments ({attachmentCount} / {MAX_ATTACHMENTS_PER_RULE})
          </h6>
          <p className="text-600 text-sm mb-2">
            Attach supporting documents (PDFs, images, Excel, etc. up to 50 MB
            each).
          </p>

          {!!attachments?.length && (
            <DataTable value={attachments} className="mb-3">
              <Column field="fileName" header="File Name" />
              <Column
                field="fileSize"
                header="Size"
                body={(row: IRuleAttachment) => formatFileSize(row.fileSize)}
              />
              <Column
                field="createdAt"
                header="Uploaded"
                body={(row: IRuleAttachment) =>
                  new Date(row.createdAt).toLocaleDateString()
                }
              />
              <Column
                header="Actions"
                body={actionsTemplate}
                style={{ width: "120px" }}
              />
            </DataTable>
          )}

          <FileUpload
            ref={fileUploadRef}
            name="contractLanguageAttachments"
            multiple
            accept={ALLOWED_ATTACHMENT_EXTENSIONS}
            maxFileSize={MAX_FILE_SIZE_BYTES}
            mode="basic"
            chooseOptions={{
              label: "Add Attachment",
              icon: "pi pi-fw pi-plus",
            }}
            onSelect={handleSelect}
            disabled={disabled || reachedAttachmentLimit}
          />
          {isUploading && <ProgressBar mode="indeterminate" className="mt-2" />}
          {reachedAttachmentLimit && (
            <small className="text-500 block mt-2">
              Attachment limit reached. Remove a file to add more.
            </small>
          )}
        </div>
      )}
    </Card>
  );
}
